import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  INTERVALS,
  availableIntervals,
  defaultInterval,
  intervalLabel,
  isUnlimited,
  overageRateText,
  type AiCreditPackageRecord,
  type PlanInterval,
  type PlanRecord,
  type PlanUsageInfo,
} from "@currencycore/shared";
import {
  ApiRequestError,
  copyPlan,
  createPlan,
  deletePlan,
  listAiCreditPackages,
  listPlans,
  updateAiCreditPackage,
} from "../lib/api.ts";
import { compact } from "../components/Charts.tsx";
import { RowMenu } from "../components/RowMenu.tsx";
import { useConfirm } from "../components/useConfirm.tsx";
import { toast } from "../components/Toast.tsx";
import { errMsg, fmtMoney } from "../lib/ui.ts";

/** Price line for a plan at the selected interval, with graceful fallbacks. */
function priceForInterval(p: PlanRecord, interval: PlanInterval): string {
  if (p.id === "free") return "Free";
  const cents = p.prices[interval];
  if ((cents ?? 0) > 0) return `${fmtMoney(cents!)} / ${intervalLabel(interval).toLowerCase()}`;
  // No price for the selected interval: fall back to the lowest priced interval it does offer.
  const priced = INTERVALS.filter((i) => (p.prices[i] ?? 0) > 0);
  if (priced.length) {
    const lowest = priced.reduce((a, b) => (p.prices[a]! <= p.prices[b]! ? a : b));
    return `${fmtMoney(p.prices[lowest]!)} / ${intervalLabel(lowest).toLowerCase()}`;
  }
  return "Custom";
}

export function PlansPage() {
  const [plans, setPlans] = useState<PlanUsageInfo[] | null>(null);
  const [selected, setSelected] = useState<PlanInterval | null>(null);
  const navigate = useNavigate();
  const { ask, dialog } = useConfirm();

  function load() {
    return listPlans().then(setPlans).catch((e) => toast(errMsg(e, "Failed to load plans.")));
  }
  useEffect(() => {
    void load();
  }, []);

  async function addPlan() {
    try {
      const p = await createPlan({ name: "New custom plan", monthlyRequests: 1_000_000, rateLimit: 1000, prices: { monthly: 9900 } });
      toast("Plan created.", true);
      navigate(`/plans/${p.id}`);
    } catch (e) {
      toast(errMsg(e, "Could not create plan."));
    }
  }
  async function doCopy(id: string) {
    try {
      const p = await copyPlan(id);
      toast("Plan copied.", true);
      navigate(`/plans/${p.id}`);
    } catch (e) {
      toast(errMsg(e, "Could not copy plan."));
    }
  }
  function confirmDelete(p: PlanUsageInfo) {
    ask({
      title: "Delete plan?",
      message: (
        <>
          Permanently delete <strong>{p.name}</strong>? This can't be undone.
        </>
      ),
      confirmLabel: "Delete plan",
      danger: true,
      run: async () => {
        try {
          await deletePlan(p.id);
          toast("Plan deleted.", true);
          load();
        } catch (e) {
          toast(e instanceof ApiRequestError ? e.body.error.message : errMsg(e, "Could not delete plan."));
        }
      },
    });
  }

  const standard = (plans ?? []).filter((p) => p.kind === "standard");
  const custom = (plans ?? []).filter((p) => p.kind === "custom");

  const available = availableIntervals(plans ?? []);
  const interval = selected ?? defaultInterval(available);

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Plans</h1>
          <p className="muted">
            Catalog of standard + custom plans. Defaults are read-only.{" "}
            <a href="https://currency-core.com/pricing" target="_blank" rel="noopener noreferrer">
              See full plan comparison →
            </a>
          </p>
        </div>
        <div className="page-head-actions">
          {available.length >= 2 && (
            <div className="segmented" role="group" aria-label="Billing interval">
              {available.map((i) => (
                <button key={i} className={i === interval ? "active" : ""} onClick={() => setSelected(i)}>{intervalLabel(i)}</button>
              ))}
            </div>
          )}
          <button className="btn btn-primary btn-sm" onClick={addPlan}>+ New plan</button>
        </div>
      </div>

      <h3 className="plans-section">Default plans</h3>
      <div className="grid grid-4 plan-grid">
        {standard.map((p) => (
          <PlanCard key={p.id} plan={p} onOpen={() => navigate(`/plans/${p.id}`)} onCopy={() => doCopy(p.id)} onDelete={() => confirmDelete(p)} interval={interval} />
        ))}
      </div>

      <div className="plans-section-row">
        <h3 className="plans-section">Custom plans</h3>
        <span className="muted">{custom.length} plan{custom.length === 1 ? "" : "s"}</span>
      </div>
      {custom.length === 0 ? (
        <div className="card card-pad"><p className="muted">No custom plans yet. Create one or copy a default.</p></div>
      ) : (
        <div className="grid grid-4 plan-grid">
          {custom.map((p) => (
            <PlanCard key={p.id} plan={p} onOpen={() => navigate(`/plans/${p.id}`)} onCopy={() => doCopy(p.id)} onDelete={() => confirmDelete(p)} interval={interval} />
          ))}
        </div>
      )}

      <h3 className="plans-section">AI credit packages</h3>
      <AiCreditPackagesCard />

      {dialog}
    </>
  );
}

/** One-time AI credit add-on packages (separate from subscription plans). Admins
 *  edit credits, price, and the Dodo product id used at checkout. */
function AiCreditPackagesCard() {
  const [pkgs, setPkgs] = useState<AiCreditPackageRecord[] | null>(null);

  useEffect(() => {
    listAiCreditPackages()
      .then((r) => setPkgs(r.items))
      .catch((e) => toast(errMsg(e, "Failed to load AI credit packages.")));
  }, []);

  function onSaved(saved: AiCreditPackageRecord) {
    setPkgs((prev) => (prev ? prev.map((p) => (p.id === saved.id ? saved : p)) : prev));
  }

  return (
    <div className="card card-pad">
      <p className="hint">
        One-time AI credit add-ons (1 credit = 1 AI request). Checkout uses the <strong>Dodo product id</strong> set
        here, falling back to the matching <code>DODO_PRODUCT_CREDITS_*</code> env var when blank. Inactive packages are
        hidden from customers.
      </p>
      {pkgs === null ? (
        <div className="page-loading"><span className="spinner" /></div>
      ) : (
        <div className="credit-pkgs">
          {pkgs.map((p) => (
            <AiCreditPackageRow key={p.id} pkg={p} onSaved={onSaved} />
          ))}
        </div>
      )}
    </div>
  );
}

function AiCreditPackageRow({
  pkg,
  onSaved,
}: {
  pkg: AiCreditPackageRecord;
  onSaved: (p: AiCreditPackageRecord) => void;
}) {
  const [credits, setCredits] = useState(String(pkg.credits));
  const [price, setPrice] = useState(String(pkg.priceCents / 100));
  const [dodo, setDodo] = useState(pkg.dodoProductId ?? "");
  const [active, setActive] = useState(pkg.active);
  const [saving, setSaving] = useState(false);

  const dirty =
    credits !== String(pkg.credits) ||
    price !== String(pkg.priceCents / 100) ||
    dodo !== (pkg.dodoProductId ?? "") ||
    active !== pkg.active;

  async function save() {
    setSaving(true);
    try {
      const saved = await updateAiCreditPackage(pkg.id, {
        credits: Math.max(1, Math.floor(Number(credits) || 0)),
        priceCents: Math.round((Number(price) || 0) * 100),
        dodoProductId: dodo.trim() || null,
        active,
      });
      onSaved(saved);
      toast("Package saved.", true);
    } catch (e) {
      toast(errMsg(e, "Could not save the package."));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className={`credit-pkg${active ? "" : " credit-pkg-inactive"}`}>
      <span className="credit-pkg-id mono muted">{pkg.id}</span>
      <div className="field">
        <label className="form-label">Credits</label>
        <input className="input" inputMode="numeric" value={credits} onChange={(e) => setCredits(e.target.value.replace(/[^\d]/g, ""))} />
      </div>
      <div className="field">
        <label className="form-label">Price (USD)</label>
        <div className="price-input">
          <span className="price-prefix">$</span>
          <input className="input" inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value.replace(/[^\d.]/g, ""))} />
        </div>
      </div>
      <div className="field credit-pkg-dodo">
        <label className="form-label">Dodo product id</label>
        <input className="input mono" value={dodo} onChange={(e) => setDodo(e.target.value.trim())} placeholder="pdt_…" />
      </div>
      <label className="check-row">
        <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
        Active
      </label>
      <button className="btn btn-primary btn-sm" onClick={save} disabled={saving || !dirty}>
        {saving ? <><span className="spinner" />Saving…</> : "Save"}
      </button>
    </div>
  );
}

function PlanCard({
  plan,
  onOpen,
  onCopy,
  onDelete,
  interval,
}: {
  plan: PlanUsageInfo;
  onOpen: () => void;
  onCopy: () => void;
  onDelete: () => void;
  interval: PlanInterval;
}) {
  const canDelete = plan.kind === "custom" && plan.orgCount === 0;
  const items = [
    { label: "Open", onClick: onOpen },
    { label: "Copy", onClick: onCopy },
    ...(canDelete ? [{ label: "Delete", danger: true, onClick: onDelete }] : []),
  ];
  return (
    <div className="card card-pad plan-card">
      <div className="plan-card-head">
        <span className={`badge ${plan.kind === "custom" ? "badge-warn" : "badge-neutral"}`}>{plan.kind}</span>
        <span onClick={(e) => e.stopPropagation()}>
          <RowMenu items={items} />
        </span>
      </div>
      <button className="plan-name-btn" onClick={onOpen}>{plan.name}</button>
      <div className="plan-price">{priceForInterval(plan, interval)}</div>
      <ul className="plan-feats">
        <li>{isUnlimited(plan.monthlyRequests) ? "Unlimited requests / mo" : `${compact(plan.monthlyRequests!)} requests / mo`}</li>
        <li className="muted">{plan.rateLimit ? `${plan.rateLimit.toLocaleString()} req/min` : "default rate limit"}</li>
        {overageRateText(plan.overageCents, plan.overageUnit) && (
          <li className="muted">overage {overageRateText(plan.overageCents, plan.overageUnit)}</li>
        )}
        <li className="muted">{plan.orgCount} org{plan.orgCount === 1 ? "" : "s"}{plan.archived ? " · archived" : ""}</li>
      </ul>
    </div>
  );
}
